import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { ApiError } from "@/lib/api";
import { Lock, Mail, Globe } from "lucide-react";

const INDIGO = "#667eea";

export function LoginPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [lang, setLang] = useState<"fr" | "en">("fr");

  const t = lang === "fr"
    ? {
        title: "Connexion",
        subtitle: "Identifiez-vous pour accéder au dashboard",
        email: "Adresse e-mail",
        password: "Mot de passe",
        button: "Se connecter",
        loading: "Connexion…",
        footer: "Plateforme paiement multi-tenant",
      }
    : {
        title: "Sign in",
        subtitle: "Log in to access your dashboard",
        email: "Email address",
        password: "Password",
        button: "Sign in",
        loading: "Signing in…",
        footer: "Multi-tenant payment platform",
      };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const me = await auth.login(email, password);
      if (me.must_change_password) {
        navigate("/change-password", { replace: true });
        return;
      }
      const isMerchantUser = !!me.merchant_id;
      const home = isMerchantUser
        ? "/merchant/overview"
        : me.role === "admin"
        ? "/admin/merchants"
        : me.role === "finance"
        ? "/wallets"
        : me.role === "it"
        ? "/it/switch-logs"
        : "/payments";
      navigate(home, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) setError(err.detail || err.title);
      else setError("Erreur réseau. Réessayez.");
    } finally {
      setLoading(false);
    }
  };

  if (auth.status === "authenticated") {
    navigate("/", { replace: true });
    return null;
  }

  return (
    <div className="relative min-h-screen min-h-[100svh] bg-white overflow-hidden" style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}>
      {/* Top bar (logo + lang) */}
      <div className="absolute top-4 left-4 right-4 lg:top-8 lg:left-10 lg:right-10 z-20 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl overflow-hidden shadow-lg bg-white flex items-center justify-center shrink-0">
            <img src="/moko-logo.png" alt="Moko Afrika" className="w-full h-full object-cover" />
          </div>
          <div className="min-w-0">
            <div className="font-bold text-[#1a1a2e] text-base sm:text-lg leading-tight truncate">Moko Booking</div>
            <div className="text-[11px] sm:text-xs text-[#8c8ca0] truncate">
              <span className="sm:hidden">Moko Afrika</span>
              <span className="hidden sm:inline">Paiement sécurisé · Moko Afrika</span>
            </div>
          </div>
        </div>

        {/* Language selector */}
        <div className="relative inline-flex items-center gap-1 bg-white border border-slate-200 rounded-lg px-1 py-1 shadow-sm shrink-0">
          <Globe size={13} className="text-slate-400 ml-1.5 mr-0.5 hidden sm:inline" />
          <button
            onClick={() => setLang("fr")}
            className={`px-2.5 sm:px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
              lang === "fr" ? "text-white" : "text-slate-600 hover:bg-slate-50"
            }`}
            style={lang === "fr" ? { background: INDIGO } : {}}
          >
            FR
          </button>
          <button
            onClick={() => setLang("en")}
            className={`px-2.5 sm:px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
              lang === "en" ? "text-white" : "text-slate-600 hover:bg-slate-50"
            }`}
            style={lang === "en" ? { background: INDIGO } : {}}
          >
            EN
          </button>
        </div>
      </div>

      {/* Background illustrations (4 corners) - aviation + fintech */}
      <div className="hidden md:block absolute top-20 left-6 z-0 opacity-95">
        <AirplaneFlyingIllustration />
      </div>
      <div className="hidden md:block absolute top-20 right-6 z-0 opacity-95">
        <PhonePlaneTicketIllustration />
      </div>
      <div className="hidden md:block absolute bottom-6 left-6 z-0 opacity-95">
        <BoardingPassCardIllustration />
      </div>
      <div className="hidden md:block absolute bottom-6 right-6 z-0 opacity-95">
        <WalletPlaneIllustration />
      </div>

      {/* Decorative blobs */}
      <div className="pointer-events-none absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl"
           style={{ background: "radial-gradient(circle, #667eea 0%, transparent 70%)" }} />
      <div className="pointer-events-none absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl"
           style={{ background: "radial-gradient(circle, #764ba2 0%, transparent 70%)" }} />

      {/* Center form */}
      <div className="relative z-10 min-h-screen min-h-[100svh] flex items-center justify-center px-3 sm:px-4 py-24 sm:py-12">
        <div className="w-full max-w-md">
          <div
            className="bg-white rounded-2xl p-6 sm:p-10"
            style={{ boxShadow: "0 20px 60px rgba(102, 126, 234, 0.15), 0 8px 24px rgba(0, 0, 0, 0.05)" }}
          >
            <div className="mb-6 sm:mb-7">
              <h1 className="text-2xl sm:text-3xl font-bold text-[#1a1a2e] mb-2">{t.title}</h1>
              <div className="text-sm text-[#8c8ca0]">{t.subtitle}</div>
            </div>

            <form onSubmit={onSubmit} className="space-y-4 sm:space-y-5">
              <div>
                <label className="block text-sm font-semibold text-[#4a4a6a] mb-2">{t.email}</label>
                <div className="relative">
                  <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#a1a5b7]" />
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="vous@gofreshpay.com"
                    autoComplete="email"
                    inputMode="email"
                    className="w-full pl-11 pr-4 py-3.5 bg-[#f5f6fa] border border-[#f5f6fa] rounded-xl text-[#1a1a2e] placeholder-[#a1a5b7] focus:bg-white focus:border-[#667eea] focus:outline-none transition-colors text-base"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#4a4a6a] mb-2">{t.password}</label>
                <div className="relative">
                  <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#a1a5b7]" />
                  <input
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    className="w-full pl-11 pr-4 py-3.5 bg-[#f5f6fa] border border-[#f5f6fa] rounded-xl text-[#1a1a2e] placeholder-[#a1a5b7] focus:bg-white focus:border-[#667eea] focus:outline-none transition-colors text-base"
                  />
                </div>
              </div>

              {error && (
                <div className="bg-red-50 text-red-700 text-sm rounded-xl px-4 py-3 border border-red-100">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="relative w-full py-3.5 rounded-xl text-white font-semibold text-base overflow-hidden group transition-transform active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed"
                style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}
              >
                <span className="relative z-10">{loading ? t.loading : t.button}</span>
                {/* Shine effect */}
                <span
                  className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700"
                  style={{
                    background:
                      "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%)",
                  }}
                />
              </button>
            </form>
          </div>

          <div className="text-center mt-6 text-xs text-[#a1a5b7]">{t.footer}</div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* SVG illustrations - aviation flying + fintech style                 */
/* ------------------------------------------------------------------ */

/** Top-left: Airplane flying through clouds with route trail */
function AirplaneFlyingIllustration() {
  return (
    <svg width="240" height="200" viewBox="0 0 240 200" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Sun */}
      <circle cx="200" cy="40" r="18" fill="#fbbf24" />
      <circle cx="200" cy="40" r="26" fill="#fbbf24" opacity="0.25" />

      {/* Back clouds */}
      <g fill="#e0e7ff">
        <ellipse cx="40" cy="60" rx="22" ry="12" />
        <ellipse cx="55" cy="55" rx="18" ry="10" />
        <ellipse cx="180" cy="100" rx="20" ry="11" />
        <ellipse cx="195" cy="95" rx="16" ry="9" />
      </g>

      {/* Front clouds */}
      <g fill="#ffffff" stroke="#c7d2fe" strokeWidth="1.5">
        <ellipse cx="200" cy="160" rx="26" ry="13" />
        <ellipse cx="220" cy="155" rx="20" ry="11" />
      </g>

      {/* Dotted route trail */}
      <path
        d="M30 170 Q90 140 120 110 Q155 75 200 60"
        stroke="#667eea"
        strokeWidth="2.5"
        strokeDasharray="4 5"
        fill="none"
        strokeLinecap="round"
      />

      {/* Start marker */}
      <circle cx="30" cy="170" r="6" fill="#48bb78" stroke="#fff" strokeWidth="2" />

      {/* AIRPLANE — main, large, flying up-right */}
      <g transform="translate(95, 80) rotate(-22)">
        {/* Fuselage */}
        <path
          d="M0 22 L65 22 L78 26 L65 30 L0 30 Q-8 26 0 22 Z"
          fill="#667eea"
        />
        {/* Cockpit window */}
        <path d="M58 24 L70 25 L70 27 L58 28 Z" fill="#1a1a2e" opacity="0.6" />
        {/* Top wing */}
        <path d="M25 22 L40 4 L52 4 L40 22 Z" fill="#5a67d8" />
        {/* Bottom wing (tail) */}
        <path d="M5 30 L18 44 L26 44 L18 30 Z" fill="#5a67d8" />
        {/* Tail fin */}
        <path d="M2 22 L-2 8 L8 8 L8 22 Z" fill="#764ba2" />
        {/* Windows row */}
        <circle cx="20" cy="26" r="1.6" fill="#fff" />
        <circle cx="28" cy="26" r="1.6" fill="#fff" />
        <circle cx="36" cy="26" r="1.6" fill="#fff" />
        <circle cx="44" cy="26" r="1.6" fill="#fff" />
        <circle cx="52" cy="26" r="1.6" fill="#fff" />
      </g>

      {/* Speed lines behind plane */}
      <line x1="60" y1="120" x2="80" y2="115" stroke="#a5b4fc" strokeWidth="2" strokeLinecap="round" />
      <line x1="50" y1="135" x2="75" y2="128" stroke="#a5b4fc" strokeWidth="2" strokeLinecap="round" opacity="0.7" />
      <line x1="55" y1="148" x2="70" y2="143" stroke="#a5b4fc" strokeWidth="2" strokeLinecap="round" opacity="0.5" />

      {/* Destination marker (end of route) */}
      <circle cx="200" cy="60" r="8" fill="#f1416c" stroke="#fff" strokeWidth="2.5" />
      <circle cx="200" cy="60" r="3" fill="#fff" />
    </svg>
  );
}

/** Top-right: Phone with mobile money paying for plane ticket */
function PhonePlaneTicketIllustration() {
  return (
    <svg width="180" height="220" viewBox="0 0 180 220" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Small plane flying around phone */}
      <g transform="translate(125, 25) rotate(-15)">
        <path d="M0 8 L26 8 L32 10 L26 12 L0 12 Q-3 10 0 8 Z" fill="#667eea" />
        <path d="M10 8 L16 1 L21 1 L16 8 Z" fill="#5a67d8" />
        <path d="M3 12 L7 18 L11 18 L7 12 Z" fill="#5a67d8" />
      </g>
      <path d="M115 50 Q130 38 150 25" stroke="#667eea" strokeWidth="1.5" strokeDasharray="2 3" fill="none" />

      {/* Phone */}
      <rect x="35" y="40" width="100" height="170" rx="14" fill="#667eea" />
      <rect x="40" y="50" width="90" height="150" rx="6" fill="#ffffff" />

      {/* Status bar */}
      <rect x="50" y="58" width="26" height="3" rx="1.5" fill="#667eea" opacity="0.3" />
      <circle cx="110" cy="60" r="2" fill="#667eea" opacity="0.3" />

      {/* Header — airline ticket */}
      <rect x="48" y="70" width="74" height="36" rx="6" fill="url(#ticketGrad)" />
      <defs>
        <linearGradient id="ticketGrad" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#667eea" />
          <stop offset="100%" stopColor="#764ba2" />
        </linearGradient>
      </defs>

      {/* Airplane icon inside ticket */}
      <g transform="translate(55, 76)">
        <path d="M0 6 L20 6 L24 7.5 L20 9 L0 9 Q-2 7.5 0 6 Z" fill="#fff" />
        <path d="M7 6 L11 1 L14 1 L11 6 Z" fill="#fff" />
        <path d="M2 9 L5 13 L8 13 L5 9 Z" fill="#fff" />
      </g>
      <rect x="83" y="80" width="35" height="3" rx="1.5" fill="#fff" opacity="0.9" />
      <rect x="83" y="88" width="28" height="3" rx="1.5" fill="#fff" opacity="0.7" />
      <rect x="83" y="96" width="22" height="3" rx="1.5" fill="#fff" opacity="0.5" />

      {/* Amount line */}
      <rect x="48" y="115" width="50" height="6" rx="3" fill="#1a1a2e" />
      <rect x="100" y="115" width="22" height="6" rx="3" fill="#48bb78" />

      <rect x="48" y="128" width="74" height="2" rx="1" fill="#cbd5e0" />
      <rect x="48" y="135" width="60" height="2" rx="1" fill="#cbd5e0" />

      {/* Success check */}
      <circle cx="85" cy="160" r="14" fill="#48bb78" />
      <path d="M78 160 L83 165 L92 154" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />

      {/* Button */}
      <rect x="50" y="182" width="70" height="12" rx="6" fill="#667eea" />

      {/* Home indicator */}
      <rect x="74" y="203" width="22" height="2" rx="1" fill="#667eea" opacity="0.5" />

      {/* Coins floating */}
      <circle cx="22" cy="100" r="8" fill="#fbbf24" />
      <text x="22" y="103.5" textAnchor="middle" fill="#fff" fontSize="8" fontWeight="bold">$</text>
    </svg>
  );
}

/** Bottom-left: Boarding pass + payment card + plane */
function BoardingPassCardIllustration() {
  return (
    <svg width="230" height="170" viewBox="0 0 230 170" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Small plane flying top */}
      <g transform="translate(170, 18) rotate(20)">
        <path d="M0 8 L24 8 L30 10 L24 12 L0 12 Q-3 10 0 8 Z" fill="#667eea" />
        <path d="M9 8 L14 2 L19 2 L14 8 Z" fill="#5a67d8" />
        <path d="M3 12 L6 17 L10 17 L6 12 Z" fill="#5a67d8" />
      </g>
      <path d="M130 70 Q150 50 170 30" stroke="#667eea" strokeWidth="1.5" strokeDasharray="2 3" fill="none" />

      {/* Back card */}
      <rect
        x="115" y="65" width="100" height="62" rx="8"
        fill="url(#bpCard)"
        transform="rotate(8 165 96)"
      />
      <defs>
        <linearGradient id="bpCard" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#667eea" />
          <stop offset="100%" stopColor="#764ba2" />
        </linearGradient>
        <linearGradient id="bpPass" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="100%" stopColor="#f5f6fa" />
        </linearGradient>
      </defs>
      {/* Chip on card */}
      <rect x="125" y="78" width="16" height="12" rx="2" fill="#ffd700" transform="rotate(8 133 84)" />
      {/* Card number */}
      <rect x="125" y="100" width="20" height="3" rx="1" fill="#fff" opacity="0.85" transform="rotate(8 135 102)" />
      <rect x="150" y="103" width="20" height="3" rx="1" fill="#fff" opacity="0.85" transform="rotate(8 160 105)" />
      <rect x="175" y="106" width="20" height="3" rx="1" fill="#fff" opacity="0.85" transform="rotate(8 185 108)" />
      <text x="195" y="120" fill="#fff" fontSize="7" fontWeight="bold" transform="rotate(8 195 117)">VISA</text>

      {/* Front boarding pass */}
      <rect x="15" y="50" width="155" height="95" rx="8" fill="url(#bpPass)" stroke="#e0e7ff" strokeWidth="1.5" />
      {/* Perforation */}
      <line x1="118" y1="55" x2="118" y2="140" stroke="#cbd5e0" strokeWidth="1" strokeDasharray="2 3" />

      {/* Header strip */}
      <rect x="15" y="50" width="155" height="20" rx="8" fill="#667eea" />
      <rect x="15" y="62" width="155" height="8" fill="#667eea" />
      {/* BOARDING PASS text */}
      <text x="22" y="64" fill="#fff" fontSize="9" fontWeight="bold" letterSpacing="1">BOARDING PASS</text>
      {/* Plane icon in header */}
      <g transform="translate(150, 56)">
        <path d="M0 5 L14 5 L18 6.5 L14 8 L0 8 Q-2 6.5 0 5 Z" fill="#fff" />
        <path d="M5 5 L8 1 L11 1 L8 5 Z" fill="#fff" />
      </g>

      {/* Passenger name */}
      <text x="22" y="83" fill="#a1a5b7" fontSize="6" fontWeight="bold" letterSpacing="0.5">PASSENGER</text>
      <text x="22" y="93" fill="#1a1a2e" fontSize="10" fontWeight="bold">J. KABILA</text>

      {/* FROM > TO */}
      <text x="22" y="108" fill="#a1a5b7" fontSize="6" fontWeight="bold">FROM</text>
      <text x="22" y="120" fill="#1a1a2e" fontSize="14" fontWeight="bold">FIH</text>

      <g transform="translate(56, 113)">
        <path d="M0 4 L14 4 L18 5.5 L14 7 L0 7 Q-2 5.5 0 4 Z" fill="#667eea" />
        <path d="M5 4 L8 0 L11 0 L8 4 Z" fill="#5a67d8" />
      </g>

      <text x="80" y="108" fill="#a1a5b7" fontSize="6" fontWeight="bold">TO</text>
      <text x="80" y="120" fill="#1a1a2e" fontSize="14" fontWeight="bold">LSI</text>

      {/* Flight */}
      <text x="22" y="135" fill="#a1a5b7" fontSize="6" fontWeight="bold">FLIGHT</text>
      <text x="46" y="135" fill="#1a1a2e" fontSize="8" fontWeight="bold">CAA213</text>
      <text x="80" y="135" fill="#a1a5b7" fontSize="6" fontWeight="bold">SEAT</text>
      <text x="100" y="135" fill="#1a1a2e" fontSize="8" fontWeight="bold">12A</text>

      {/* QR code (right side of pass) */}
      <rect x="128" y="80" width="34" height="34" rx="2" fill="#1a1a2e" />
      <g fill="#fff">
        <rect x="132" y="84" width="6" height="6" />
        <rect x="152" y="84" width="6" height="6" />
        <rect x="132" y="104" width="6" height="6" />
        <rect x="140" y="92" width="3" height="3" />
        <rect x="146" y="98" width="3" height="3" />
        <rect x="140" y="104" width="3" height="3" />
        <rect x="148" y="92" width="3" height="3" />
        <rect x="144" y="86" width="3" height="3" />
        <rect x="152" y="100" width="3" height="3" />
      </g>
      <text x="145" y="124" textAnchor="middle" fill="#1a1a2e" fontSize="6" fontWeight="bold">SCAN</text>
    </svg>
  );
}

/** Bottom-right: Wallet with boarding pass + small plane */
function WalletPlaneIllustration() {
  return (
    <svg width="200" height="180" viewBox="0 0 200 180" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Plane silhouette flying top */}
      <g transform="translate(30, 20) rotate(15)">
        <path d="M0 10 L30 10 L36 12 L30 14 L0 14 Q-3 12 0 10 Z" fill="#667eea" />
        <path d="M10 10 L16 3 L21 3 L16 10 Z" fill="#5a67d8" />
        <path d="M3 14 L7 20 L11 20 L7 14 Z" fill="#5a67d8" />
        <circle cx="22" cy="12" r="1.2" fill="#fff" />
        <circle cx="26" cy="12" r="1.2" fill="#fff" />
      </g>
      <path d="M50 60 Q35 50 28 35" stroke="#667eea" strokeWidth="1.5" strokeDasharray="2 3" fill="none" />

      {/* Boarding pass sticking out */}
      <rect
        x="40" y="55" width="80" height="40" rx="4"
        fill="#ffffff"
        stroke="#e0e7ff" strokeWidth="1.5"
        transform="rotate(-6 80 75)"
      />
      <rect x="40" y="55" width="80" height="10" rx="4" fill="#667eea" transform="rotate(-6 80 60)" />
      <g transform="translate(46, 58.5) rotate(-6 0 0)">
        <text fill="#fff" fontSize="6" fontWeight="bold">FLIGHT CAA213</text>
      </g>
      <g transform="translate(46, 76) rotate(-6 0 0)">
        <text fill="#1a1a2e" fontSize="8" fontWeight="bold">FIH</text>
        <text x="22" fill="#667eea" fontSize="6">→</text>
        <text x="32" fill="#1a1a2e" fontSize="8" fontWeight="bold">LSI</text>
      </g>

      {/* Cash bill */}
      <rect
        x="90" y="50" width="60" height="30" rx="3"
        fill="#48bb78"
        transform="rotate(8 120 65)"
      />
      <circle cx="120" cy="65" r="6" fill="#fff" opacity="0.85" transform="rotate(8 120 65)" />
      <text x="120" y="68.5" textAnchor="middle" fill="#48bb78" fontSize="8" fontWeight="bold" transform="rotate(8 120 65)">$</text>

      {/* Wallet body */}
      <rect x="20" y="90" width="160" height="78" rx="10" fill="url(#walletPlaneGrad)" />
      <defs>
        <linearGradient id="walletPlaneGrad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#667eea" />
          <stop offset="100%" stopColor="#5a67d8" />
        </linearGradient>
      </defs>
      {/* Wallet fold */}
      <rect x="20" y="90" width="160" height="18" rx="10" fill="#5a67d8" />
      {/* Clip */}
      <circle cx="155" cy="128" r="7" fill="#fff" />
      <circle cx="155" cy="128" r="3" fill="#667eea" />

      {/* Card slot on wallet */}
      <rect x="32" y="120" width="80" height="30" rx="3" fill="#fff" opacity="0.15" />
      <g transform="translate(40, 128)">
        <rect width="10" height="8" rx="1.5" fill="#ffd700" />
      </g>
      <text x="62" y="138" fill="#fff" fontSize="6" fontWeight="bold" opacity="0.9">**** 4242</text>

      {/* Plane icon on wallet (corner badge) */}
      <circle cx="160" cy="100" r="9" fill="#fff" opacity="0.95" />
      <g transform="translate(154, 97)">
        <path d="M0 3 L10 3 L13 4 L10 5 L0 5 Q-1 4 0 3 Z" fill="#667eea" />
        <path d="M4 3 L6 0.5 L8 0.5 L6 3 Z" fill="#5a67d8" />
      </g>
    </svg>
  );
}
